import { useElementWidth } from '../../../../common/ui/width-observer';

export const REFRESH_FILTER_ROW_WIDTH_ID = 'search-page-refresh-filter-row';
export const REFRESH_FILTER_ROW_APPS_WIDTH_ID = 'search-page-refresh-filter-row-apps';

const FILTERS_CONTROL_WIDTH = 32;
// `contentFit="compact"` caps an inline filter at 180px, so budget for the widest it can get.
const INLINE_FILTER_MAX_WIDTH = 180;
const INLINE_FILTER_GAP = 4;
const MIN_GROUP_SEPARATION = 8;

// An unselected app tile is icon-only; a selected one, or every one while "All apps" is selected
// on a wide enough row, expands to show its label. Both keep 12px padding either side, so an
// icon-only tile is 12 + 20px logo + 12.
const APP_TILE_WIDTH = 44;
const APP_PILL_WIDTH = 124;
const ALL_APPS_PILL_WIDTH = 96;
const MORE_APPS_CONTROL_WIDTH = 100;
const APPS_GAP = 6;
// Fits "All apps", five labelled pills, More apps and the Filters control.
const MIN_ROW_WIDTH_FOR_ALL_APPS_LABELS = 900;

// The single measurement both halves of the row read; the app area is measured at its
// natural width, so neither side's own content can feed back into it.
export const useRefreshFilterRowWidths = (): { rowWidth: number; appsWidth: number } => ({
	rowWidth: useElementWidth(REFRESH_FILTER_ROW_WIDTH_ID),
	appsWidth: useElementWidth(REFRESH_FILTER_ROW_APPS_WIDTH_ID),
});

// `undefined` means not measured yet, so no cap applies.
export const maxInlineFiltersForWidth = ({
	rowWidth,
	appsWidth,
}: {
	rowWidth: number;
	appsWidth: number;
}): number | undefined => {
	if (rowWidth === 0) {
		return undefined;
	}

	const available = rowWidth - appsWidth - FILTERS_CONTROL_WIDTH - MIN_GROUP_SEPARATION;

	return Math.max(0, Math.floor(available / (INLINE_FILTER_MAX_WIDTH + INLINE_FILTER_GAP)));
};

export const useMaxInlineFilters = (): number | undefined =>
	maxInlineFiltersForWidth(useRefreshFilterRowWidths());

// The app-side budget only has to answer "how many tiles fit", assuming inline filters have
// already been squeezed to zero: that's the point at which the apps group is the only thing
// left to give up space, so `rowWidth` minus the always-present Filters control is the whole
// budget. "All apps" and the selected app (when there is one) are never optional — only plain
// tiles are, since they have somewhere to go: the More apps dropdown. Each plain tile is
// budgeted at the width it renders at, labelled or icon-only.
//
// `willRenderMoreApps` decides whether MORE_APPS_CONTROL_WIDTH is reserved. The caller resolves
// this from the row's own width-trimmed, threshold-and-absorption-aware overflow — not the
// unconstrained app list — so the reservation and the actual render decision can never disagree.
export const maxVisibleAppsForWidth = ({
	rowWidth,
	isAppSelected,
	areAppsLabelled,
	willRenderMoreApps,
}: {
	rowWidth: number;
	isAppSelected: boolean;
	areAppsLabelled: boolean;
	willRenderMoreApps: boolean;
}): number | undefined => {
	if (rowWidth === 0) {
		return undefined;
	}

	const available =
		rowWidth -
		FILTERS_CONTROL_WIDTH -
		MIN_GROUP_SEPARATION -
		APPS_GAP -
		(willRenderMoreApps ? MORE_APPS_CONTROL_WIDTH : 0);

	const mandatoryWidth = ALL_APPS_PILL_WIDTH + (isAppSelected ? APP_PILL_WIDTH + APPS_GAP : 0);
	const remainingForTiles = available - mandatoryWidth;
	const tileWidth = areAppsLabelled ? APP_PILL_WIDTH : APP_TILE_WIDTH;
	const extraTiles = Math.max(0, Math.floor(remainingForTiles / (tileWidth + APPS_GAP)));

	return (isAppSelected ? 1 : 0) + extraTiles;
};

export const useMaxVisibleApps = (
	isAppSelected: boolean,
	areAppsLabelled: boolean,
	willRenderMoreApps: boolean,
): number | undefined =>
	maxVisibleAppsForWidth({
		rowWidth: useElementWidth(REFRESH_FILTER_ROW_WIDTH_ID),
		isAppSelected,
		areAppsLabelled,
		willRenderMoreApps,
	});

// `0` means not measured yet, so labels show until the row proves too narrow.
export const areAllAppsLabelsShownForWidth = (rowWidth: number): boolean =>
	rowWidth === 0 || rowWidth >= MIN_ROW_WIDTH_FOR_ALL_APPS_LABELS;
